// `?lang=es|en` en la URL (la landing lo manda al vincular y al registro,
// CC-CONSOLE#1561). Una preferencia que la persona ya guardó manda: si existe,
// el idioma de la URL se aplica SIN guardarse (`changeLanguageWithoutPersisting`);
// sólo sin preferencia se aplica y se guarda. Cualquier otro valor se ignora.
//
// `i18n/index` se carga bajo demanda: arrastra todos los textos y su
// inicialización, y este módulo lo importan pantallas (y pruebas) ligeras.

/** Misma llave que i18n/index.ts. */
const LANGUAGE_STORAGE_KEY = "app-language-preference";

export const langFromSearch = (search: string): "es" | "en" | null => {
  const value = new URLSearchParams(search).get("lang")?.trim().toLowerCase();
  return value === "es" || value === "en" ? value : null;
};

/** ¿La persona ya eligió idioma en este navegador? */
export const hasStoredLanguage = (): boolean => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "es" || stored === "en";
  } catch {
    return false;
  }
};

type I18nModule = {
  default: { language?: string; changeLanguage: (lng: string) => unknown };
  changeLanguageWithoutPersisting: (lng: string) => unknown;
};

const loadI18n = (): Promise<I18nModule> => import("../i18n") as unknown as Promise<I18nModule>;

/**
 * Aplica el idioma de la URL si es válido y distinto del actual. Devuelve el
 * idioma leído (o null) en seguida; el cambio ocurre al cargar i18n. Nunca lanza.
 */
export const applyLangParam = (search: string, load: () => Promise<I18nModule> = loadI18n): "es" | "en" | null => {
  const lang = langFromSearch(search);
  if (!lang) return null;
  const persist = !hasStoredLanguage();
  void load()
    .then((mod) => {
      if (String(mod.default.language || "").startsWith(lang)) return;
      if (persist) void mod.default.changeLanguage(lang);
      else void mod.changeLanguageWithoutPersisting(lang);
    })
    .catch(() => undefined);
  return lang;
};
